import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button, Field, Input, Spinner } from '@zentera/ui';
import { api, ApiError } from '@zentera/web-core';
import { meKey, useMe } from '../lib/auth';

function Layout({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-subtle">
      <div className="flex flex-1 flex-col items-center px-4 pt-[14vh] pb-10">
        <div className="w-full max-w-[360px]">
          <div className="mb-8 flex items-center gap-2">
            <img src="/zentera-mark.svg" alt="" className="size-[22px]" />
            <span className="text-[14px] font-semibold">Zentera Workspace</span>
          </div>
          <h1 className="text-lg font-medium">{title}</h1>
          {description && <p className="mt-1 text-sm text-fg-3">{description}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="rounded-md border border-[#f3c9c5] bg-danger-subtle px-3 py-2 text-sm text-danger">
      {error instanceof ApiError ? error.message : 'Não foi possível concluir.'}
    </p>
  );
}

const safeNext = (raw: string | null) => (raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/');

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => api.post<{ mustChangePassword: boolean }>('/api/auth/login', { email, password }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: meKey });
      void navigate(r.mustChangePassword ? '/password' : safeNext(params.get('next')));
    },
  });
  return (
    <Layout title="Entrar" description="Acesso da equipe do provedor.">
      <form className="space-y-4" noValidate onSubmit={(e) => { e.preventDefault(); login.mutate(); }}>
        <FormError error={login.error} />
        <Field label="E-mail"><Input type="email" autoComplete="username" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Senha"><Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        <Button type="submit" variant="primary" className="w-full" loading={login.isPending} disabled={!email || !password}>Entrar</Button>
      </form>
    </Layout>
  );
}

export function PasswordPage() {
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const change = useMutation({
    mutationFn: () => api.post('/api/auth/password', { currentPassword: current, newPassword: next }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meKey });
      void navigate('/');
    },
  });
  const errors = change.error instanceof ApiError ? change.error.fieldErrors() : {};
  const mismatch = confirm.length > 0 && confirm !== next;
  if (me.isPending) return <div className="flex h-screen items-center justify-center"><Spinner /></div>;
  if (me.isError) return <Navigate to="/login" replace />;
  return (
    <Layout
      title={me.data.user.mustChangePassword ? 'Defina sua senha' : 'Alterar senha'}
      description="Mínimo de 12 caracteres, com letras e números."
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!mismatch) change.mutate(); }}>
        {!errors.newPassword && <FormError error={change.error} />}
        <Field label="Senha atual"><Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
        <Field label="Nova senha" error={errors.newPassword}><Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} /></Field>
        <Field label="Confirme a nova senha" error={mismatch ? 'As senhas não coincidem' : undefined}><Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
        <Button type="submit" variant="primary" className="w-full" loading={change.isPending} disabled={!current || next.length < 12 || mismatch}>Salvar senha</Button>
      </form>
    </Layout>
  );
}
